/**
 * Proofreading in the browser (ADR-0002; D-0059: Harper, entirely on the
 * device). One `WorkerLinter` per page, its WebAssembly served by the portal
 * as a build asset and compiled in a Web Worker, so linting never blocks the
 * interface and no clinical text leaves the browser. The shared configuration
 * in `@beam/core` sets the default rules explicitly (Harper's quirk: without
 * it the grammar rules stay silently inactive), turns the suppressed rules
 * off and loads the dictionary the view carries.
 *
 * Nothing here talks to a server. Ignored suggestions are kept as Harper's
 * privacy-respecting hashes in this browser's storage, keyed by the person;
 * no lint text, dictionary entry or ignored phrase reaches telemetry of any
 * kind, because there is none.
 */

import { configureProofreader, houseMessage } from '@beam/core';
import { Dialect, WorkerLinter, type Lint, type Linter } from 'harper.js';
import { binary } from 'harper.js/binary';
import { useCallback, useEffect, useRef, useState } from 'react';

/** A suggestion as the popover shows it: the span into the box text, the words, the category, the replacements. */
export type Issue = {
  id: string;
  start: number;
  end: number;
  /** Harper's lint kind: Spelling, Grammar, Punctuation and so on. A category, never text. */
  kind: string;
  kindPretty: string;
  message: string;
  problem: string;
  replacements: string[];
  lint: Lint;
};

/** What Apply writes through the box's existing path: the span the suggestion was made against, the replacement, the category. */
export type ProofChange = { start: number; end: number; before: string; after: string; rule: string };

export const LINT_DEBOUNCE_MS = 600;

let shared: Promise<Linter> | null = null;
const loaded = new Set<string>();

/** The page's one linter, set up on first use; later calls add any new dictionary words. */
export function proofreader(dictionary: readonly string[]): Promise<Linter> {
  if (!shared) {
    shared = (async () => {
      const linter = new WorkerLinter({ binary, dialect: Dialect.Australian });
      await linter.setup();
      await configureProofreader(linter, dictionary);
      for (const w of dictionary) loaded.add(w);
      return linter;
    })();
    return shared;
  }
  return shared.then(async (linter) => {
    const fresh = dictionary.filter((w) => !loaded.has(w));
    if (fresh.length > 0) {
      await configureProofreader({ getDefaultLintConfig: () => linter.getLintConfig(), setLintConfig: () => Promise.resolve(), importWords: (words) => linter.importWords(words) }, fresh);
      for (const w of fresh) loaded.add(w);
    }
    return linter;
  });
}

const ignoredKey = (userKey: string) => `beam.proofreading.ignored.${userKey}`;

function restoreIgnored(linter: Linter, userKey: string): Promise<void> {
  try {
    const json = localStorage.getItem(ignoredKey(userKey));
    return json ? linter.importIgnoredLints(json) : Promise.resolve();
  } catch {
    return Promise.resolve();
  }
}

async function persistIgnored(linter: Linter, userKey: string): Promise<void> {
  try {
    const exported = await linter.exportIgnoredLints();
    localStorage.setItem(ignoredKey(userKey), typeof exported === 'string' ? exported : JSON.stringify(exported));
  } catch (err) {
    // Storage may be unavailable (a private window); the ignore still holds for this page. The warning carries no text: hashes only.
    console.warn('proofreading: the ignored suggestions could not be kept for the next visit', err instanceof Error ? err.message : String(err));
  }
}

/** The document-level facts every box reads (the D-0059 amendment): on or off for this reader, the dictionary, who may add to it. */
export type ProofreadingSettings = {
  on: boolean;
  dictionary: string[];
  /** Keys the ignored-suggestion store to the person: the preference belongs to the reader. */
  userKey: string;
  /** The doctor may add to the dictionary (it is the doctor's registry record); QA ignores instead. */
  canAddWord: boolean;
  /** Each box reports its open issue count so the page switch can carry the total. */
  report: (boxId: string, count: number) => void;
  /** The linter is set up and the first lints are on their way: the page switch stops saying "loading". */
  onReady?: () => void;
};

export const PROOFREADING_OFF: ProofreadingSettings = { on: false, dictionary: [], userKey: '', canAddWord: false, report: () => {} };

/**
 * Lint one box's text. Debounced while the person types; paused while
 * dictation is landing (interim text is not the document); re-lints once the
 * committed text changes. Off means no linter is created at all.
 */
export function useProofreading({ boxId, text, enabled, paused, settings }: { boxId: string; text: string; enabled: boolean; paused: boolean; settings: ProofreadingSettings }): {
  issues: Issue[];
  ready: boolean;
  failed: boolean;
  apply: (issue: Issue, replacement: number) => Promise<ProofChange | null>;
  ignore: (issue: Issue) => Promise<void>;
  addWord: (word: string) => Promise<void>;
} {
  const ctx = settings;
  const [issues, setIssues] = useState<Issue[]>([]);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const linterRef = useRef<Linter | null>(null);
  const restored = useRef(false);
  const run = useRef(0);
  const dictionary = ctx.dictionary;

  const free = (list: Issue[]) => { for (const i of list) { try { i.lint.free(); } catch { /* already freed */ } } };

  useEffect(() => {
    if (!enabled) { setIssues((prev) => { free(prev); return []; }); ctx.report(boxId, 0); return; }
    let cancelled = false;
    proofreader(dictionary).then(async (linter) => {
      if (cancelled) return;
      linterRef.current = linter;
      // The restore of ignored hashes never gates readiness: a slow or refused restore loses the ignores, not the underlines.
      if (!restored.current) { restored.current = true; await Promise.race([restoreIgnored(linter, ctx.userKey).catch(() => undefined), new Promise((r) => setTimeout(r, 2000))]); }
      if (cancelled) return;
      setReady(true);
      ctx.onReady?.();
    }).catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, dictionary]);

  useEffect(() => {
    if (!enabled || !ready || paused) return;
    const linter = linterRef.current;
    if (!linter) return;
    const mine = ++run.current;
    const timer = setTimeout(async () => {
      try {
        const lints = await linter.lint(text, { language: 'plaintext' });
        if (mine !== run.current) { for (const l of lints) l.free(); return; }
        const next: Issue[] = lints.map((lint, i) => {
          const span = lint.span();
          // Harper offers the same replacement text more than once (two rules, one word); the popover shows each text once.
          const replacements = [...new Set(lint.suggestions().map((s) => s.get_replacement_text()))];
          return { id: `${i}-${span.start}-${span.end}`, start: span.start, end: span.end, kind: lint.lint_kind(), kindPretty: lint.lint_kind_pretty(), message: houseMessage(lint.message()), problem: lint.get_problem_text(), replacements, lint };
        }).filter((i) => i.end > i.start && i.end <= text.length);
        setIssues((prev) => { free(prev); return next; });
        ctx.report(boxId, next.length);
      } catch {
        setFailed(true);
      }
    }, LINT_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, enabled, ready, paused]);

  useEffect(() => () => { ctx.report(boxId, 0); }, [boxId]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = useCallback(async (issue: Issue, replacement: number): Promise<ProofChange | null> => {
    const linter = linterRef.current;
    if (!linter) return null;
    // The index is into the de-duplicated list, so the suggestion is found by its text.
    const wanted = issue.replacements[replacement];
    const suggestion = wanted === undefined ? undefined : issue.lint.suggestions().find((s) => s.get_replacement_text() === wanted);
    if (!suggestion) return null;
    const applied = await linter.applySuggestion(text, issue.lint, suggestion);
    // The change is confined to the span: everything after it is the tail of the source, unchanged.
    const after = applied.slice(issue.start, applied.length - (text.length - issue.end));
    return { start: issue.start, end: issue.end, before: text.slice(issue.start, issue.end), after, rule: issue.kind };
  }, [text]);

  const ignore = useCallback(async (issue: Issue) => {
    const linter = linterRef.current;
    if (!linter) return;
    // The plural call: in harper.js 2.7.0 the singular `ignoreLint` records nothing (proven in test/proofreading.test.ts).
    await linter.ignoreLints(text, [issue.lint]);
    await persistIgnored(linter, ctx.userKey);
    setIssues((prev) => prev.filter((i) => i.id !== issue.id));
    run.current += 1;
  }, [text, ctx.userKey]);

  const addWord = useCallback(async (word: string) => {
    const linter = linterRef.current;
    if (!linter) return;
    await linter.importWords([word]);
    loaded.add(word);
    setIssues((prev) => prev.filter((i) => i.problem !== word));
  }, []);

  return { issues, ready, failed, apply, ignore, addWord };
}
